import React, { useState, useEffect } from "react"
import { getManageEstateMail } from "../../../api/manage_estate_mail"
import { manageEstateUpdateMail } from "../../../api/manage_estate_update_mail"
import { useNavigate } from "react-router-dom"
import { useLoading } from "../../LoadingContext"
import { useConnectionError } from "../../ConnectionErrorContext"

import DashboardLayout from "../../components/DashboardLayout"
import DashboardNavbar from "../../components/DashboardNavbar"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDInput from "components/MDInput"
import Card from "@mui/material/Card"
import MDButton from "components/MDButton"

export interface ManageEstateData {
  id: string
  catv_id: string
  type: string
  name: string
  address: string
  phone: string
  create_date: number
  update_date: number
  is_delete: number
  facility_count: number
}

const ManageEstateMail: React.FC = () => {
  const { startLoading, stopLoading } = useLoading()
  const { showConnectionError } = useConnectionError()
  const navigate = useNavigate()

  // 開発者アカウントでの閲覧を可能とする
  const [isViewMode, setIsViewMode] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>("")

  if (localStorage.getItem("session_code") == "") {
    navigate("/login")
    return <></>
  }

  const validate = (mails: string[]) => {
    setErrorMessage("")
    let hasError = false

    // 絵文字を除外するための正規表現
    const emojiRegex = /(?:\p{Extended_Pictographic}(?![\u3190-\u319F\u3300-\u33FF]))+/gu

    // 必須項目のバリデーション
    for (let i = 0; i < mails.length && !hasError; i++) {
      if (!mails[i]) {
        setErrorMessage("必須項目が入力されていません")
        hasError = true
      } else if (mails[i].length > 64) {
        setErrorMessage("入力文字数は64文字以内です")
        hasError = true
      } else if (mails[i].includes(",")) {
        setErrorMessage("メールアドレスにカンマは使用できません")
        hasError = true
      } else if (emojiRegex.test(mails[i])) {
        setErrorMessage("絵文字は使用できません")
        hasError = true
      }
    }
    return !hasError
  }

  const fetchManageEstateMail = async () => {
    try {
      startLoading()
      const manageEstateData = await getManageEstateMail()
      console.log(manageEstateData)

      // カンマ区切りを配列に変換、空文字なら空配列に
      let detection_mail = manageEstateData.detection_mail ? manageEstateData.detection_mail.split(",") : []

      // 要素数を10に固定
      if (detection_mail.length < 10) {
        // 足りない分を空文字で埋める
        detection_mail = [...detection_mail, ...Array(10 - detection_mail.length).fill("")]
      } else if (detection_mail.length > 10) {
        // 多すぎる場合は先頭10件だけにする
        detection_mail = detection_mail.slice(0, 10)
      }

      setDetectionMail(detection_mail)

      // 開発者アカウントでの閲覧を可能とする
      const auth = localStorage.getItem("account_type")
      if (auth == "service") {
        let isViewModeWork = true
        const view_mode = localStorage.getItem("view_mode")
        if (view_mode == "2") {
          const estate_id = localStorage.getItem("show_mode_estate")
          if (!(estate_id === "" || estate_id === "-")) {
            isViewModeWork = false
          }
        }
        setIsViewMode(isViewModeWork)
      }
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        navigate("/login")
      } else if (!error.response) {
        showConnectionError(true)
      }
    } finally {
      stopLoading()
    }
  }

  useEffect(() => {
    fetchManageEstateMail()
  }, [])

  const [detectionMail, setDetectionMail] = useState<string[]>([])
  const handleChange = (index: number, str: string) => {
    setDetectionMail((prev) => {
      const updated = [...prev]
      updated[index] = str
      return updated
    })
  }
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      startLoading()
      //空でない文字列だけ残す
      const filtered = detectionMail.filter((mail) => mail && mail.trim() !== "")
      //バリデーション
      if (!validate(filtered)) {
        return
      }
      //カンマ区切りに変換
      const detectionMailStr = filtered.join(",")
      //通信処理
      await manageEstateUpdateMail(detectionMailStr)
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        navigate("/login") // セッション切れ時にトップページへリダイレクト
      } else if (error.response && (error.response.status === 403 || error.response.status === 404)) {
        const errorDetail = error.response.data.detail
        setErrorMessage(errorDetail.message)
      } else if (error.response) {
        const errorDetail = error.response.data.detail
        setErrorMessage(errorDetail.message)
      } else {
        setErrorMessage("サーバーへの接続に失敗しました")
      }
    } finally {
      stopLoading()
    }
  }

  return (
    <DashboardLayout>
      <MDBox mb={4}>
        <DashboardNavbar absolute={true} />
      </MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" pt={3} pb={3}>
        <MDBox>
          <MDTypography variant="h4" gutterBottom>
            検知メール送信先設定
          </MDTypography>
        </MDBox>
        <MDBox style={{ height: "20px" }}></MDBox>
      </MDBox>
      <Card
        sx={{
          width: "100%",
          maxHeight: "calc(100vh - 100px)",
          padding: "20px",
          display: "grid",
          gridTemplateColumns: "1fr 1fr", // 2列
          gap: 2, // 行・列の間隔
          boxSizing: "border-box"
        }}
      >
        {detectionMail.map((mail, index) => (
          <MDInput
            key={"mail" + index}
            label={`メールアドレス ${index + 1}`}
            type="text"
            name={"mail" + index}
            value={mail}
            onChange={(e) => handleChange(index, e.target.value)}
            sx={{
              width: "100%", // 各セル内で全幅にする
              boxSizing: "border-box"
            }}
            disabled={isViewMode ? true : false}
          />
        ))}

        {!isViewMode && (
          <>
            <MDBox
              gridColumn="1 / -1"
              mt={2}
              mb={2}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              {errorMessage !== "" && (
                <MDBox
                  color="#FF0000" // テキスト色
                  mb={2} // 下マージン
                  fontSize="0.7em" // フォントサイズ
                >
                  {errorMessage}
                </MDBox>
              )}
              <MDButton
                variant="contained"
                color="info"
                fullWidth
                sx={{
                  maxWidth: "250px",
                  borderRadius: "4px",
                  textTransform: "none",
                  fontSize: "1rem",
                  padding: "0.75rem 0",
                  backgroundColor: "#1976d2",
                  "&:hover": {
                    backgroundColor: "#1565c0"
                  }
                }}
                onClick={(e) => {
                  handleSubmit(e)
                }}
              >
                変更
              </MDButton>
            </MDBox>
          </>
        )}
      </Card>
    </DashboardLayout>
  )
}

export default ManageEstateMail
